import { useState, forwardRef } from 'react';

interface VideoResult {
  id: string;
  title: string;
  channelTitle: string;
  thumbnailUrl: string;
}

interface VideoSearchProps {
  onVideoSelect: (videoId: string, title: string) => void;
}

const VideoSearch = forwardRef<HTMLInputElement, VideoSearchProps>(({ onVideoSelect }, ref) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<VideoResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!query.trim()) {
      setError('Please enter a search query');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResults([]);

      const apiKey = import.meta.env.VITE_YOUTUBE_API_KEY;
      if (!apiKey) {
        throw new Error('YouTube API key is not configured');
      }

      const url = new URL('https://www.googleapis.com/youtube/v3/search');
      const params = {
        part: 'snippet',
        q: query.trim(),
        type: 'video',
        maxResults: '10',
        key: apiKey,
        order: 'relevance',
      };

      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });

      console.log('Search Request URL:', url.toString());

      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        console.error('Full YouTube API Error:', data);
        const errorDetails = data.error?.errors?.[0]?.reason || data.error?.message;
        if (errorDetails === 'quotaExceeded') {
          throw new Error('Search quota exceeded for today. Please try again tomorrow. The rest of the app is still functional.');
        }
        throw new Error(`API Error: ${errorDetails}`);
      }

      console.log('API Response Data:', data);

      if (!data.items || !Array.isArray(data.items)) {
        throw new Error('Invalid response format from YouTube API');
      }

      const formattedResults = data.items
        .filter((item: any) => item.id?.videoId && item.snippet)
        .map((item: any) => ({
          id: item.id.videoId,
          title: item.snippet.title,
          channelTitle: item.snippet.channelTitle,
          thumbnailUrl: item.snippet.thumbnails?.medium?.url || item.snippet.thumbnails?.default?.url,
        }));

      setResults(formattedResults);
    } catch (err) {
      console.error('Video search error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="mt-4">
      {/* Search Bar */}
      <div className="flex gap-2 mb-4">
        <input
          ref={ref}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Search for videos..."
          className="flex-1 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
        />
        <button
          onClick={handleSearch}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-800 transition-colors"
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <p className="text-center text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse flex space-x-4 bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
              <div className="w-32 h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {!loading && !error && results.length === 0 && query && (
        <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <p className="text-center text-gray-500 dark:text-gray-400">No results found</p>
        </div>
      )}

      {!loading && !error && results.length > 0 && (
        <div className="space-y-4">
          {results.map((result) => (
            <button
              key={result.id}
              onClick={() => onVideoSelect(result.id, result.title)}
              className="w-full flex items-start gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
            >
              <img
                src={result.thumbnailUrl}
                alt={result.title}
                className="w-32 h-20 object-cover rounded-lg"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
                  {result.title}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {result.channelTitle}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default VideoSearch; 